const SESSION_KEYS = [
  'authTokens',
  'user',
  'selectedServiceCenter',
  'lastLoginDate',
  'otpLastSentTime',
  'access_token',
  'refresh_token',
  'user_full_name',
];

const AUTH_QUERY_KEYS = [
  'service_hint',
  'return_to',
  'auth_source',
  'access_token',
  'refresh_token',
  'user_full_name',
  'sso_ts',
];

const trimTrailingSlash = (value) => String(value || '').replace(/\/+$/, '');

const isTrue = (value) => String(value || '').trim().toLowerCase() === 'true';

export const isUnifiedAuthEnabled = () => isTrue(process.env.REACT_APP_ENABLE_UNIFIED_AUTH);

const deriveAuthWebBase = () => {
  const explicitWeb = trimTrailingSlash(process.env.REACT_APP_AUTH_WEB_URL || '');
  if (explicitWeb) {
    return explicitWeb;
  }

  const explicitApi = trimTrailingSlash(process.env.REACT_APP_AUTH_API_URL || '');
  if (explicitApi) {
    try {
      const parsed = new URL(explicitApi, window.location.origin);
      let pathname = trimTrailingSlash(parsed.pathname || '/');
      if (pathname.endsWith('/api')) {
        pathname = pathname.slice(0, -4);
      }
      parsed.pathname = pathname || '/';
      parsed.search = '';
      parsed.hash = '';
      return trimTrailingSlash(parsed.toString());
    } catch {
      // ignore and fallback below
    }
  }

  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (isLocalhost) {
    return 'http://localhost:8001';
  }

  return trimTrailingSlash(window.location.origin);
};

export const UNIFIED_AUTH_WEB_URL = deriveAuthWebBase();

export const hasExplicitUnifiedAuthConfig = () => Boolean(
  isUnifiedAuthEnabled()
  && (
    trimTrailingSlash(process.env.REACT_APP_AUTH_WEB_URL || '')
    || trimTrailingSlash(process.env.REACT_APP_AUTH_API_URL || '')
  )
);

export const isLikelyMisconfiguredUnifiedAuth = () => {
  if (!isUnifiedAuthEnabled()) {
    return true;
  }

  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (isLocalhost || hasExplicitUnifiedAuthConfig()) {
    return false;
  }

  return trimTrailingSlash(UNIFIED_AUTH_WEB_URL) === trimTrailingSlash(window.location.origin);
};

export const clearLocalSession = () => {
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
};

const toRelativePath = (value) => {
  if (!value) {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }

  if (typeof value === 'string') {
    if (value.startsWith('/')) {
      return value;
    }
    try {
      const parsed = new URL(value, window.location.origin);
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return `${window.location.pathname}${window.location.search}${window.location.hash}`;
    }
  }

  if (typeof value === 'object' && value.pathname) {
    return `${value.pathname}${value.search || ''}${value.hash || ''}`;
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

export const sanitizeUnifiedAuthReturnPath = (value) => {
  try {
    const parsed = new URL(toRelativePath(value), window.location.origin);

    AUTH_QUERY_KEYS.forEach((key) => {
      parsed.searchParams.delete(key);
    });

    if (parsed.pathname === '/login') {
      parsed.pathname = '/';
      parsed.search = '';
      parsed.hash = '';
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return '/';
  }
};

export const buildUnifiedAuthUrl = (nextPath) => {
  const authUrl = new URL(UNIFIED_AUTH_WEB_URL, window.location.origin);
  const returnTo = new URL(sanitizeUnifiedAuthReturnPath(nextPath), window.location.origin).toString();
  authUrl.searchParams.set('service_hint', 'sklad');
  authUrl.searchParams.set('return_to', returnTo);
  return authUrl.toString();
};

const buildLocalLoginUrl = (nextPath) => {
  const loginUrl = new URL('/login', window.location.origin);
  const returnPath = sanitizeUnifiedAuthReturnPath(nextPath);
  if (returnPath && returnPath !== '/') {
    loginUrl.searchParams.set('return_to', returnPath);
  }
  return loginUrl.toString();
};

export const redirectToUnifiedAuth = (nextPath) => {
  if (!isUnifiedAuthEnabled() || isLikelyMisconfiguredUnifiedAuth()) {
    window.location.assign(buildLocalLoginUrl(nextPath));
    return;
  }

  window.location.assign(buildUnifiedAuthUrl(nextPath));
};

export const consumeSsoTokensFromUrl = () => {
  if (!isUnifiedAuthEnabled()) {
    return null;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    const authSource = params.get('auth_source');
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const userFullName = params.get('user_full_name');

    if (authSource !== 'auth_service' || !accessToken) {
      return null;
    }

    const tokens = refreshToken ? { access: accessToken, refresh: refreshToken } : { access: accessToken };
    localStorage.setItem('authTokens', JSON.stringify(tokens));
    if (userFullName) {
      localStorage.setItem('user_full_name', userFullName);
    }

    params.delete('auth_source');
    params.delete('access_token');
    params.delete('refresh_token');
    params.delete('user_full_name');
    params.delete('sso_ts');

    const cleanedSearch = params.toString();
    const cleanedUrl = `${window.location.pathname}${cleanedSearch ? `?${cleanedSearch}` : ''}${window.location.hash || ''}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash || ''}`;
    if (cleanedUrl !== currentUrl) {
      window.history.replaceState(null, '', cleanedUrl);
    }

    return tokens;
  } catch {
    return null;
  }
};
